import { CommonModule } from '@angular/common';
import { Component, computed, effect, model, signal, untracked } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FieldType } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { DEFAULT_DECIMAL_PRECISION, xAdd } from '@phoenix/common';
import { CurrencyCode } from '@phx-erp/shared';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { Subject } from 'rxjs';
import { DecimalPlacesCustomPipe } from '../../../../@core/pipes';
import { I18nService } from '../../../../@core/services/i18n/i18n.service';
import { CURRENCY_SYMBOL_MAP } from '../../../../app.config';
import { Document, DocumentDirection, DocumentLineItem, OpenStockView, OpenStockViewSearchInput, PriceSourceType, StockAction } from '../../../../generated/generated-types';
import { ServerSettingsService } from '../../../../shared/services/server-settings.service';
import { ProductService } from '../../../products/services/product.service';
import { OpenStockViewService } from '../../../warehouse/services/open-stock-view.service';
import { LineItemDataService } from '../../services/line-item-data.service';
import { StockDataInput, StorageQuantityInfoDliStatusbarComponent } from '../storage-quantity-info-dli-statusbar/storage-quantity-info-dli-statusbar.component';

@Component({
  selector: 'app-formly-document-line-item-statusbar',
  templateUrl: './formly-document-line-item-statusbar.component.html',
  providers: [
    OpenStockViewService,
  ],
  imports: [
    CommonModule,
    DecimalPlacesCustomPipe,
    TranslatePipe,
    MessageModule,
    SkeletonModule,
    TooltipModule,
    ButtonModule,
    StorageQuantityInfoDliStatusbarComponent
  ]
})
export class FormlyDocumentLineItemStatusbarComponent extends FieldType {

  decimalPlacesInputSettings: number = DEFAULT_DECIMAL_PRECISION;
  stockInfos: OpenStockView[];
  DocumentDirection = DocumentDirection;
  StockAction = StockAction;
  PriceSourceType = PriceSourceType;
  //totalStock: number;
  stockQuantityInStoragePlace: number;

  dli = model<DocumentLineItem>();
  doc = model<Document>();
  originQuantity = signal<number>(null);

  cachedDliUpdatedAt = signal<Date>(null);

  openStockSearchInput: OpenStockViewSearchInput = {}

  selectedLineItems = computed(() => this.lineItemStatusService.selectedLineItems());

  hasSelection = computed(() => (this.selectedLineItems()?.length ?? 0) > 0);

  isSingleSelection = computed(() => (this.selectedLineItems()?.length ?? 0) === 1);

  /**
   * Sum of selected line-item revenues. Re-reads `dli` so inline edits refresh aggregates.
   */
  aggregatedRevenue = computed(() => {
    const items = this.selectedLineItems();
    this.lineItemStatusService.dli();
    if (!items?.length || items.every((item) => item.revenue == null)) {
      return null;
    }
    return items.reduce((sum, item) => xAdd(sum, item.revenue ?? 0), 0);
  });

  /**
   * Sum of selected line-item net totals (basis for revenue %).
   * Prefer proratedLinePrice (matches document.total); fall back to discountedLinePrice.
   */
  aggregatedTotal = computed(() => {
    const items = this.selectedLineItems();
    this.lineItemStatusService.dli();
    if (!items?.length) {
      return null;
    }
    const lineTotals = items.map(
      (item) => item.proratedLinePrice ?? item.discountedLinePrice ?? null,
    );
    if (lineTotals.every((value) => value == null)) {
      return null;
    }
    return lineTotals.reduce((sum, value) => xAdd(sum, value ?? 0), 0);
  });

  /**
   * null if nothing should be shown, otherwise the percentage
   * when i have 100€ in total and 10€ in revenue, i want to show 10%
   */
  revenuePercent = computed(() => {
    const revenue = this.aggregatedRevenue();
    const total = this.aggregatedTotal();
    if (revenue == null || total == null || total === 0 || revenue === total) {
      return null;
    }
    return (revenue / total) * 100;
  });

  /**
   * Price origin label for selection: shared origin, or "various" when mixed.
   */
  priceOriginLabel = computed(() => {
    const items = this.selectedLineItems();
    this.lineItemStatusService.dli();
    if (!items?.length) {
      return null;
    }
    const sources = new Set(items.map((item) => item.priceSource ?? null));
    if (sources.size !== 1) {
      return this.i18n.translate(_('document-line-item.price-origin-various'));
    }
    const [source] = sources;
    return source != null ? this.getPriceOrigin(source) : null;
  });

  isManualPriceOrigin = computed(() => {
    const items = this.selectedLineItems();
    this.lineItemStatusService.dli();
    if (!items?.length) {
      return false;
    }
    const sources = new Set(items.map((item) => item.priceSource ?? null));
    return sources.size === 1 && sources.has(PriceSourceType.MANUALPRICE);
  });

  stockDataArrayInput = computed<StockDataInput[]>(() => {
    return [{
      name: this.i18n.translate('warehouse.all-storage-places'),
      storagePlaceId: '',
      ordered: this.lineItemStatusService.orderedStock() ?? 0,
      reserved: this.lineItemStatusService.reservedStock() ?? 0,
      openSales: this.lineItemStatusService.freeTotalStock() ?? 0,
      totalStock: this.lineItemStatusService.totalStock() ?? 0,
      quarantineStock: this.lineItemStatusService.quarantineStock() ?? 0
    },
    {
      name: this.dli()?.storagePlace?.identifier || this.doc()?.storagePlace?.identifier,
      storagePlaceId: this.dli()?.storagePlace?.id || this.doc()?.storagePlace?.id || '',
      ordered: this.lineItemStatusService.orderedStockInCurrentStorageLocation() ?? 0,
      reserved: this.lineItemStatusService.reservedStockInCurrentStorageLocation() ?? 0,
      openSales: this.lineItemStatusService.freeStockCurrentWarehouse() ?? 0,
      totalStock: this.lineItemStatusService.stockCurrentWarehouse() ?? 0,
      quarantineStock: this.lineItemStatusService.quarantineStockCurrentStorageLocation() ?? null //for now not supported - needs better solution
    }
    ]
  });

  liveChangesCurrentWarehouse = model<Partial<StockDataInput>>();

  protected destroy$ = new Subject<void>();

  constructor(
    public lineItemStatusService: LineItemDataService,
    private serverSettingsService: ServerSettingsService,
    private productService: ProductService,
    private i18n: I18nService,
  ) {
    super();

    effect(() => {
      let dli = this.lineItemStatusService.dli();
      untracked(() => {
        this.dli.set(dli);
      })
    });

    effect(() => {
      let doc = this.lineItemStatusService.doc();
      untracked(() => {
        this.doc.set(doc);
      })
    });

    // set originQuantity
    effect(() => {
      let origQuantity = this.lineItemStatusService.storeQuantity();
      untracked(() => {
        if (!this.originQuantity() || this.cachedDliUpdatedAt() !== this.dli()?.updatedAt)
          this.originQuantity.set(origQuantity);
      });
    });

    effect(() => {
      let newQuantity = this.lineItemStatusService.storeQuantity();
      untracked(() => {
        if (this.originQuantity() !== newQuantity && this.cachedDliUpdatedAt() && this.cachedDliUpdatedAt() === this.dli()?.updatedAt) {
          let dli = this.lineItemStatusService.dli();
          const docDef = this.doc().documentDefinition;
          const direction = docDef?.documentDirection;
          const stockAction = docDef?.stockAction;
          let liveChanges: Partial<StockDataInput> = {};
          if (dli?.storagePlace?.id) {
            liveChanges.storagePlaceId = dli?.storagePlace?.id;
          }
          if (direction == DocumentDirection.OUT && stockAction == StockAction.RESERVE) {
            liveChanges.reserved = newQuantity;
            liveChanges.openSales = -newQuantity;
          }
          if (direction == DocumentDirection.IN && stockAction == StockAction.RESERVE) {
            liveChanges.ordered = newQuantity;
          }
          if (direction == DocumentDirection.OUT && stockAction == StockAction.OUT) {
            liveChanges.openSales = -newQuantity;
            liveChanges.totalStock = -newQuantity;
          }
          if (direction == DocumentDirection.OUT && stockAction == StockAction.IN) {
            liveChanges.openSales = newQuantity;
            liveChanges.totalStock = newQuantity;
          }
          if (direction == DocumentDirection.IN && stockAction == StockAction.IN) {
            liveChanges.openSales = -newQuantity;
            liveChanges.totalStock = -newQuantity;
          }
          if (direction == DocumentDirection.IN && stockAction == StockAction.OUT) {
            liveChanges.openSales = newQuantity;
            liveChanges.totalStock = newQuantity;
          }
          //TODO: when quarantine is implemented on storagePlace level - needs also to be handled here
          this.liveChangesCurrentWarehouse.set(liveChanges);
        }
        else {
          this.liveChangesCurrentWarehouse.set(null);
        }
      });
    });

    effect(() => {
      let dli = this.lineItemStatusService.dli();
      untracked(() => {
        if (dli) {
          this.cachedDliUpdatedAt.set(dli.updatedAt);
        } else {
          this.cachedDliUpdatedAt.set(null);
        }
      });
    });
  }

  /**
   * @deprecated use it from app.config.ts
   */
  currencySymbolMap = {
    USD: '$',
    EUR: '€',
    GBP: '£',
  };

  priceOriginMap = {
    MANUALPRICE: this.i18n.translate('documents.manual'),
    LIST: this.i18n.translate('documents.list'),
    PRICELIST: this.i18n.translate('documents.price-list'),
    CUSTOMERPRICE: this.i18n.translate('documents.customer-price'),
    ACCESSORYCONFIG: this.i18n.translate('documents.accessory-config-price'),
  };

  ngAfterViewInit() {
    if (!this.decimalPlacesInputSettings)
      this.serverSettingsService.getRoundDecimalPlacesLineItem().then((x) => {
        this.decimalPlacesInputSettings = x;
      });
    if (!this.decimalPlacesInputSettings)
      this.serverSettingsService.getRoundDecimalPlaces().then((x) => {
        this.decimalPlacesInputSettings = x;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getCurrencySymbol(currencyCode: CurrencyCode) {
    return CURRENCY_SYMBOL_MAP[currencyCode] || currencyCode;
  }

  getPriceOrigin(priceOrigin: PriceSourceType) {
    return this.priceOriginMap[priceOrigin];
  }

  async getProduct(productId: string) {
    return await this.productService.getItemData(productId)
  }

  getQuantityBackedByStock() {
    let quantity = this.lineItemStatusService.dli()?.lineItemQuantityStatus?.quantityBackedByStock;
    if (this.doc().documentDefinition?.documentDirection == DocumentDirection.OUT) {
      if (quantity > 0) {
        return `(+${quantity})`
      }
      else {
        return `(-${Math.abs(quantity)})`
      }
    }
    else {
      if (quantity > 0) {
        return `(-${quantity})`
      }
      else {
        return `(+${Math.abs(quantity)})`
      }
    }
  }

  getQuantityBackedByStockTooltip() {
    if (this.doc().documentDefinition?.documentDirection == DocumentDirection.OUT) {
      if (this.doc().documentDefinition?.stockAction === StockAction.RESERVE) {
        return this.i18n.translate('document-line-item.edit-dli-quantity-backed-by-stock-reserve-doc-out')
      } else if (this.doc().documentDefinition?.stockAction === StockAction.OUT || this.doc().documentDefinition?.stockAction === StockAction.IN) {
        return this.i18n.translate('document-line-item.edit-dli-quantity-backed-by-stock-out-in-doc-out')
      }
    }
    else {
      if (this.doc().documentDefinition?.stockAction === StockAction.OUT || this.doc().documentDefinition?.stockAction === StockAction.IN) {
        return this.i18n.translate('document-line-item.edit-dli-quantity-backed-by-stock-out-in-doc-in')
      }
    }
    return;
  }

  showQuantityBackedByStock() {
    return ((this.lineItemStatusService.doc()?.documentDefinition
      ?.documentDirection == DocumentDirection.OUT &&
      this.lineItemStatusService.doc()?.documentDefinition?.stockAction ==
      StockAction.RESERVE) ||
      this.lineItemStatusService.doc()?.documentDefinition?.stockAction ===
      StockAction.IN ||
      this.lineItemStatusService.doc()?.documentDefinition?.stockAction ===
      StockAction.OUT) &&
      this.lineItemStatusService.dli()?.lineItemQuantityStatus
        ?.quantityBackedByStock !== undefined &&
      this.lineItemStatusService.dli()?.lineItemQuantityStatus
        ?.quantityBackedByStock !== null
  }


}
